import { defineStore } from 'pinia'
import { toRaw } from 'vue'
import { useBrowseByStore } from './browseBy'
import { useBrowseStore } from './browse'
import useEventsBus from '@/shared/composables/eventBus'
import type { Entity } from '@/services/db'
import CatalogDetails from '@/models/catalogDetails'
import type CatalogPriceGroup from '@/models/catalogPriceGroup'
import type LinkedCustomer from '@/models/linkedCustomer'
import type CustomerSegmentation from '@/models/customerSegmentation'
import { type FilterCriteria, FilterCriteriaMode } from '@/models/filterCriteria'
import UserProfile from '@/models/userProfile'
import UserPreferences from '@/models/userPreferences'
import SellerDetails from '@/models/sellerDetails'
import { getUserProfile } from '@/api/t1/user'
import workerWrapper from '@/services/workerWrapper'
import auth from '@/services/auth'
import appConfig from '@/services/appConfig'
import utils from '@/services/utils'
import { getArticleStates, getArticlesCount } from '@/api/t1/article'
import type { ArticleFilterModel, ArticleStateModel } from '@/api/t1/model/articleModel'
import type { MasterSizeScaleModel, SizeScaleModel } from '@/api/t1/model/sizeModel'
import { getMasterSizeScales, getSellerAvailableSizeScales } from '@/api/t1/size'
import { getRestrictedBucketAttributes } from '@/api/t1/buckets'
import type DuneAsset from '@/models/duneAsset'
import { getSilhouetteAssets } from '@/api/dune/asset'
import { getSellerConfigurations, getSellerLookup } from '@/api/t1/sellers'
import type { SellerLookupDefinitionModel } from '@/api/t1/model/sellerModel'
import { appConstants, containerTypeConstants, personalSettingsConstants, privileges, thumbnailConstants } from '@/models/constants'
import type ConfirmDialog from '@/shared/components/ConfirmDialog.vue'
import { getRolesBasedRestrictedAttributes, getRolesBasedRestrictedRetailWindows, getRolesBasedRestrictedSegmentations } from '@/api/t1/catalog'
import { getContainer, getMyContainers } from '@/api/t1/container'
import type { ISettingItem } from '@/modules/articleDetails/components/SettingsDropdown.vue'
import type { ISettingItemWhiteboard } from '@/modules/whiteboard/components/Settings.vue'
import type Article from '@/models/article'

// TODO: Need to work on separating user data and catalog data.
export const useUserStore = defineStore({
  id: 'userStore',
  state: () => ({
    currentUsername: '',
    currentUserType: '',
    mainNavBarVisibility: true,
    userProfile: undefined as unknown as UserProfile,
    userPreferences: undefined as unknown as UserPreferences,
    groupByCol: undefined as string | undefined,
    groupByRow: undefined as string | undefined,
    sortBy: undefined as string | undefined,
    activeCatalog: undefined as CatalogDetails | undefined,
    linkedCatalogDetails: {} as Record<number, CatalogDetails>,
    otherLinkedCatalogDetails: {} as Record<number, CatalogDetails>,
    sellerDetails: undefined as SellerDetails | undefined,
    myAttributes: undefined as Record<string, IMyAttribute> | undefined,
    priceGroups: { retail: undefined as CatalogPriceGroup | undefined, wholesale: undefined as CatalogPriceGroup | undefined, outlet: undefined as CatalogPriceGroup | undefined },
    currentCustomer: undefined as LinkedCustomer | undefined,
    currentCustomerSegmentations: undefined as CustomerSegmentation | undefined,
    totalArticles: 0,
    hooked: false,
    thumbnailSize: 3,
    currentTree: undefined as ITreeNode[] | undefined,
    totalUsers: 0,
    articleStateList: [] as Array<ArticleStateModel>,
    masterSizeScales: {} as Record<number, MasterSizeScaleModel>,
    sizeScales: {} as Record<number, SizeScaleModel>,
    indexedLookupAttributeDefinition: {} as Record<string, SellerLookupDefinitionModel>,
    customSortArticlePropertyAndValues: {} as Record<string, string[]>,
    indexedSilhouetteImages: {} as Record<string, DuneAsset>,
    notificationsLastVisited: new Date(),
    indexedCatalogDetailsFromAdmin: {} as Record<number, CatalogDetails>,
    indexedRequestSourceArticlesFromAdmin: {} as Record<number, Article>,
    confirmDialogEl: undefined as InstanceType<typeof ConfirmDialog> | undefined,
    settingsContainer: {} as Record<string, any>,
    defaultImageTypeForTemplates: 'firstImage',
    sellerConfig: {} as Record<string, any> | undefined,
  }),
  actions: {
    async doInitCatalog(catalogCode: number, customerId: number, retailPriceId: number, wholesalePriceId: number, outletPriceId: number, progress: (progress: number) => void) {
      if (this.userProfile) {
        this.userProfile.catalogCode = catalogCode
      }
      else {
        this.setProfile(catalogCode)
      }

      this.resetCatalogData()
      if (this.activeCatalog === undefined) {
        const catalogDetails = new CatalogDetails(await appConfig.DB!.getCatalogDetails(catalogCode), this.userProfile.Roles)
        this.activeCatalog = catalogDetails
        await this.setLinkedCatalogs(catalogDetails)
      }

      this.sellerDetails = new SellerDetails(await appConfig.DB!.getSellerDetails(this.activeCatalog.AccountId))
      localStorage.setItem('currentAccountId', '')
      this.activeCatalog.TreeDefinitions = await appConfig.DB!.getCatalogTreeDefinition(catalogCode)
      // if (this.activeCatalog.DataSourceTypeId === appConstants.catalogTypes.inherited) {
      //   const masterCatalog = await appConfig.DB!.getCatalogDetails(this.activeCatalog.MasterCatalogCode)
      //   this.activeCatalog.ShipmentWindowRangeList = masterCatalog.ShipmentWindowRangeList
      // }
      if (customerId < 0) {
        this.currentCustomer = undefined
      }
      else {
        this.currentCustomer = await appConfig.DB!.getLinkedCustomer(catalogCode, customerId)
      }
      await this.setCurrentCustomerSegmentations()
      if (this.activeCatalog.CatalogPriceGroupList.length) {
        this.activeCatalog.CatalogPriceGroupList.forEach((catalogPriceGroup) => {
          if (catalogPriceGroup.Id === retailPriceId) {
            this.priceGroups.retail = catalogPriceGroup
          }
          else if (catalogPriceGroup.Id === wholesalePriceId) {
            this.priceGroups.wholesale = catalogPriceGroup
          }
          else if (catalogPriceGroup.Id === outletPriceId) {
            this.priceGroups.outlet = catalogPriceGroup
          }
        })
      }

      appConfig.DB!.trackChanges = false
      // TODO: I am removing the sizes and allocations module for performance reasons, we must bring it back if the catalog has the Orders or Forecast module enabled!
      const entitiesToLoad: Array<Entity> = ['Resources', 'MyFavoriteTags', 'SavedViews', 'ColorPallets']
      if (this.activeCatalog.RequestsEnabled > 0) {
        entitiesToLoad.push('Requests')
      }
      const isOrderModuleEnabled = !this.activeCatalog.Config.DisableModules.join(',').toLowerCase().split(',').includes('order')
      if (isOrderModuleEnabled) {
        entitiesToLoad.push('Locations')
        entitiesToLoad.push('CustomerSegmentations')
      }
      await this.doLoadData(entitiesToLoad, undefined, false, progress)
      appConfig.DB!.trackChanges = true

      // default image types for templates
      this.defaultImageTypeForTemplates = this.activeCatalog.Config.NewestImageAssetKeyList && this.activeCatalog.Config.NewestImageAssetKeyList.length ? 'newestImage' : 'firstImage'

      getSilhouetteAssets(this.activeCatalog.DuneContext, this.activeCatalog.ContextKey)
        .then((resp) => {
          if (resp.data.length) {
            resp.data.forEach((imageData) => {
              this.indexedSilhouetteImages[imageData.Key.toLowerCase()] = imageData
            })
          }
        })
        .catch(e => console.warn('Could not load silhouette assets', e))

      try {
        this.articleStateList = await getArticleStates(this.activeCatalog.AccountId, this.activeCatalog.AttributeGroupId)
      }
      catch (error) {
        console.warn('Could not load article state list', error)
      }

      getMasterSizeScales(this.activeCatalog.AccountId)
        .then(data => this.masterSizeScales = utils.arrayToNumberDictionary(data, 'Id'))
        .catch(e => console.warn('Could not load MasterSizeScale list', e))

      getSellerAvailableSizeScales(this.activeCatalog.AccountId)
        .then(data => this.sizeScales = utils.arrayToNumberDictionary(data, 'Id'))
        .catch(e => console.warn('Could not load SizeScale list', e))

      // apply role based restriction data to bucket attributes
      try {
        const roleBasedRestrictedBucketAttributesResponse = await getRestrictedBucketAttributes(this.activeCatalog.CatalogCode)
        this.activeCatalog.updateBucketAttributeListBasedOnUserRoleRestriction(roleBasedRestrictedBucketAttributesResponse.data, this.userProfile)
      }
      catch (error) {
        console.warn('could not apply user role restrictions', error)
      }
      try {
        const roleBasedRestrictedAttributesResponse = await getRolesBasedRestrictedAttributes(this.activeCatalog.CatalogCode)
        this.activeCatalog.updateRolesBasedRestrictedAttributes(roleBasedRestrictedAttributesResponse.data, this.userProfile)
      }
      catch (error) {
        console.warn('could not apply user role restrictions', error)
      }
      try {
        const roleBasedRestrictedRetailWindowsResponse = await getRolesBasedRestrictedRetailWindows(this.activeCatalog.CatalogCode)
        this.activeCatalog.updateRolesBasedRestrictedRetailWindows(roleBasedRestrictedRetailWindowsResponse.data, this.userProfile)
      }
      catch (error) {
        console.warn('could not apply user role restrictions', error)
      }
      try {
        const roleBasedRestrictedSegmentationsResponse = await getRolesBasedRestrictedSegmentations(this.activeCatalog.CatalogCode)
        this.activeCatalog.updateRolesBasedRestrictedSegmentations(roleBasedRestrictedSegmentationsResponse.data, this.userProfile)
      }
      catch (error) {
        console.warn('could not apply user role restrictions', error)
      }
      // for Inherited Catalog dont show not assorted articles articles if the UI_ViewUnAssortedArticles is not assigned.
      let isViewUnAssortedArticlesRestricted: boolean = false
      if (this.activeCatalog.DataSourceTypeId === appConstants.catalogTypes.inherited && !this.userProfile.isValidPrivilege(privileges.article.viewUnAssortedArticles)) {
        isViewUnAssortedArticlesRestricted = true
      }
      const activeArticleStateList = this.articleStateList.filter(articleState => articleState.Status)

      // Load MyCatalog
      this.myAttributes = await appConfig.DB!.buildMyAttributes(this.activeCatalog, this.linkedCatalogDetails, this.currentUsername, this.userProfile.Id, true, this.currentCustomer, this.currentCustomerSegmentations, activeArticleStateList, isViewUnAssortedArticlesRestricted)

      getSellerLookup(this.activeCatalog.AccountId, this.activeCatalog.AttributeGroupId, this.myAttributes)
        .then((data) => {
          if (data) {
            this.indexedLookupAttributeDefinition = data
          }
        })
        .catch(e => console.warn('Could not load seller lookups', e))

      // Make sure the default favorite tag is created
      // reimplement this not correct
      // const defaultTag = await appConfig.DB!.myFavorites.get({ CatalogCode: catalogCode, UserId: this.userProfile.Id, CreatedByUserName: this.currentUsername, Tag: 'Default' })
      // if (!defaultTag) {
      //   await appConfig.DB!.myFavorites.add(new FavoriteTag(catalogCode, this.userProfile.Id, { Id: utils.hashCode('Default'), Tag: 'Default', Color: '#32C2E0', CreatedByUserName: this.currentUsername, Status: 1 }))
      // }

      await appConfig.DB!.getResourceTypes()

      const modelDetailSettingsContainerName = `${containerTypeConstants.modelDetailSettings}-${this.userProfile.Id}`
      const containersList = await utils.tryAsync(getMyContainers(catalogCode, containerTypeConstants.modelDetailSettings))
      if (containersList.success) {
        const container = containersList.result.data.find(f => f.ContainerName === modelDetailSettingsContainerName)
        if (container) {
          const containerDetails = await utils.tryAsync(getContainer(container.Id))
          if (containerDetails.success && containerDetails.result && containerDetails.result.data) {
            const containerValue = utils.tryParse(containerDetails.result.data.Value)
            if (containerValue) {
              let settings: Record<number, ISettingItem> = {}
              if (this.userPreferences.modelDetails?.settings) {
                settings = toRaw(this.userPreferences.modelDetails.settings)
              }
              if (containerValue) {
                settings[catalogCode] = containerValue
              }

              appConfig.DB!.userPreferences.update(this.userPreferences.userId, { modelDetails: { settings } })
                .then(() => {
                  this.userPreferences.modelDetails.settings = settings
                })
                .catch(e => console.error('Unable to update userPreferences', e))
            }
          }
        }
      }

      const whiteboardSettingsContainerName = `${containerTypeConstants.whiteboardSettings}-${this.userProfile.Id}`
      const whiteboardContainersList = await utils.tryAsync(getMyContainers(catalogCode, containerTypeConstants.whiteboardSettings))
      if (whiteboardContainersList.success) {
        const container = whiteboardContainersList.result.data.find(f => f.ContainerName === whiteboardSettingsContainerName)
        if (container) {
          const containerDetails = await utils.tryAsync(getContainer(container.Id))
          if (containerDetails.success && containerDetails.result && containerDetails.result.data) {
            const containerValue = utils.tryParse(containerDetails.result.data.Value)
            if (containerValue) {
              let settings: Record<number, ISettingItemWhiteboard> = {}
              if (this.userPreferences.whiteboard?.settings) {
                settings = toRaw(this.userPreferences.whiteboard.settings)
              }
              if (containerValue) {
                settings[catalogCode] = containerValue
              }

              appConfig.DB!.userPreferences.update(this.userPreferences.userId, { whiteboard: { settings } })
                .then(() => {
                  this.userPreferences.whiteboard.settings = settings
                })
                .catch(e => console.error('Unable to update userPreferences', e))
            }
          }
        }
      }
      // Set total articles
      this.setTotalArticles(this.activeCatalog)
      this.setDefaultModelImageType()
    },
    setDefaultModelImageType() {
      if (this.activeCatalog && this.activeCatalog.Config.DefaultImageTypeForModel && this.activeCatalog.Config.DefaultImageTypeForModel !== '') {
        const browseStore = useBrowseStore()
        browseStore.setBrowseByModelImageType(this.activeCatalog.Config.DefaultImageTypeForModel)
        if (this.activeCatalog?.Config.AvailableImageTypes && this.activeCatalog?.Config.AvailableImageTypes.model) {
          const configuredValue = this.activeCatalog?.Config.AvailableImageTypes.model.find(value => value.toString().toLowerCase() === this.activeCatalog!.Config.DefaultImageTypeForModel.toLowerCase())
          if (configuredValue) {
            browseStore.setBrowseByModelImageType(configuredValue)
          }
        }
      }
    },
    async setCurrentCustomerSegmentations() {
      if (this.activeCatalog) {
        if (utils.isDefined(this.currentCustomer)) {
          this.currentCustomerSegmentations = await appConfig.DB!.getCurrentCustomerSegmentations(this.activeCatalog.AccountId, this.activeCatalog.CatalogCode, this.currentCustomer!.CustomerId)
        }
        else {
          this.currentCustomerSegmentations = undefined
        }
      }
    },
    async doLoadData(entitiesToLoad: Entity[], catalogCodes?: number[], notifyUpdate = true, progress?: (progress: number) => void, newArticleIds?: string[]) {
      const catalogs = catalogCodes || (this.activeCatalog ? [this.activeCatalog.CatalogCode] : [])
      if (catalogs && catalogs.length > 0) {
        // for Inherited Catalog dont show not assorted articles articles if the UI_ViewUnAssortedArticles is not assigned.
        let isViewUnAssortedArticlesRestricted: boolean = false
        if (this.activeCatalog && this.activeCatalog.DataSourceTypeId === appConstants.catalogTypes.inherited && !this.userProfile.isValidPrivilege(privileges.article.viewUnAssortedArticles)) {
          isViewUnAssortedArticlesRestricted = true
        }
        if (entitiesToLoad.length) {
          await workerWrapper.sendAsync({ id: '', action: 'LoadCatalogData', content: { token: auth.getLocalToken()!, userId: this.userProfile?.Id, catalogCodes: catalogs, isViewUnAssortedArticlesRestricted, entitiesToLoad } }, progress)
        }
        if (notifyUpdate) {
          const { emitter } = useEventsBus()
          emitter('catalogDataUpdated', { source: 'Full', newArticleIds })
        }
      }
    },
    async setMyAttributes() {
      if (this.activeCatalog) {
        // for Inherited Catalog dont show not assorted articles articles if the UI_ViewUnAssortedArticles is not assigned.
        let isViewUnAssortedArticlesRestricted: boolean = false
        if (this.activeCatalog.DataSourceTypeId === appConstants.catalogTypes.inherited && !this.userProfile.isValidPrivilege(privileges.article.viewUnAssortedArticles)) {
          isViewUnAssortedArticlesRestricted = true
        }
        const activeArticleStateList = this.articleStateList.filter(articleState => articleState.Status)
        this.myAttributes = await appConfig.DB!.buildMyAttributes(this.activeCatalog, this.linkedCatalogDetails, this.currentUsername, this.userProfile.Id, true, this.currentCustomer, this.currentCustomerSegmentations, activeArticleStateList, isViewUnAssortedArticlesRestricted)
      }
    },
    async setUserProfile(catalogCode: number = 0) {
      if (this.userProfile) {
        this.userProfile.catalogCode = catalogCode
      }
      else {
        await this.setProfile(catalogCode)
      }
    },

    async setProfile(catalogCode: number = 0) {
      await getUserProfile()
        .then(async (resp) => {
          this.userProfile = new UserProfile(resp.data, catalogCode)
          // load user preferences
          let userPreferences = await appConfig.DB!.userPreferences.get(this.userProfile.Id)
          if (!userPreferences) {
            userPreferences = new UserPreferences({ userId: this.userProfile.Id, targetSummary: { filterCriteria: [] }, modelDetails: { settings: {} }, whiteboard: { settings: {} } })
            await appConfig.DB!.userPreferences.put(userPreferences)
          }
          else if (!userPreferences.modelDetails) {
            userPreferences.modelDetails = { settings: {} }
            await appConfig.DB!.userPreferences.put(userPreferences)
          }
          else if (!userPreferences.whiteboard) {
            userPreferences.whiteboard = { settings: {} }
            await appConfig.DB!.userPreferences.put(userPreferences)
          }
          this.userPreferences = userPreferences
        })
        .catch(ex => console.error('Error: fetching profile API', ex))
    },
    async doLoadTree(treeDefinitionId: number, customerId: number) {
      if (this.activeCatalog) {
        this.currentTree = await appConfig.DB!.buildTree(this.activeCatalog, treeDefinitionId, customerId, this.currentCustomerSegmentations, this.myAttributes)
      }
    },
    setActiveCatalog(catalog: CatalogDetails) {
      this.activeCatalog = catalog
    },
    setNotificationsLastVisited() {
      this.notificationsLastVisited = new Date()
    },
    async setLinkedCatalogs(catalog: CatalogDetails) {
      this.linkedCatalogDetails = {}
      this.otherLinkedCatalogDetails = {}
      if (catalog.LinkedCatalogList.length) {
        for (let index = 0; index < catalog.LinkedCatalogList.length; index++) {
          const linkedCatalog = catalog.LinkedCatalogList[index]
          if (index < 2) {
            this.linkedCatalogDetails[linkedCatalog.CatalogCode] = await appConfig.DB!.getCatalogDetails(linkedCatalog.CatalogCode)
          }
          else {
            this.otherLinkedCatalogDetails[linkedCatalog.CatalogCode] = await appConfig.DB!.getCatalogDetails(linkedCatalog.CatalogCode)
          }
        }
      }
    },
    async setTotalArticles(catalog: CatalogDetails, criteria?: FilterCriteria[]) {
      const browseByStore = useBrowseByStore()

      const filters: ArticleFilterModel[] = []
      if (criteria) {
        const seasonIndex = criteria.findIndex(c => c.attribute === '_Seasons')
        if (criteria && seasonIndex >= 0 && criteria[seasonIndex].multipleVals?.length) {
          filters.push({ attribute: '_Seasons', mode: FilterCriteriaMode.multiString, value: criteria[seasonIndex].multipleVals, exclude: false })
        }
      }

      if (utils.isDefined(this.currentCustomer) && utils.isDefined(this.currentCustomerSegmentations) && this.currentCustomerSegmentations.Segmentations.length > 0) { // not fullrange filter only segmentaed articles
        filters.push({
          attribute: '_CustomerSegmentations',
          mode: FilterCriteriaMode.multiString,
          value: this.currentCustomerSegmentations.Segmentations.filter(s => s.Status > 0).map(s => s.SegmentationId),
          exclude: false,
        })
      }

      // if logged in as customer and no active segmentation do not call API
      if (!utils.isDefined(this.currentCustomer) || (utils.isDefined(this.currentCustomer) && utils.isDefined(this.currentCustomerSegmentations) && this.currentCustomerSegmentations.Segmentations.length !== 0)) {
        this.totalArticles = await getArticlesCount(catalog.CatalogCode, { firstArticle: (browseByStore.isBrowseByModel ? true : undefined), body: { filters } })
      }
      if (!browseByStore.isBrowseByModel && catalog.RequestsEnabled) {
        const requestsCount = await appConfig.DB!.requests.where({ CatalogCode: catalog.CatalogCode, IsCreateArticleRequest: 1 }).count()
        this.totalArticles += requestsCount
      }
    },
    setGroupBy(col: string | undefined, row: string | undefined) {
      this.groupByCol = col
      this.groupByRow = row
    },
    setThumbnailSize(size: number) {
      this.thumbnailSize = size
    },
    setSortBy(prop: string | undefined) {
      this.sortBy = prop
      if (this.sortBy !== '_customSort') {
        // reset customSortArticlePropertyAndValues
        this.setCustomSortArticlePropertyAndValues({})
      }
    },
    setToken(token: IT1AuthToken) {
      this.currentUsername = token.unique_name
      this.currentUserType = token.type
    },
    setTotalUser(length: number) {
      this.totalUsers = length
    },
    unsetToken() {
      this.currentUsername = ''
      auth.clearLocalToken()
    },
    setCustomSortArticlePropertyAndValues(customSortArticlePropertyAndValues: Record<string, string[]>) {
      this.customSortArticlePropertyAndValues = customSortArticlePropertyAndValues
    },
    setConfirmDialogEl(confirmDialogEl: InstanceType<typeof ConfirmDialog> | undefined) {
      this.confirmDialogEl = confirmDialogEl
    },
    resetCatalogData() {
      // if (full) {
      //   this.activeCatalog = undefined
      //   this.linkedCatalogDetails = {}
      // }
      this.activeCatalog = undefined
      this.groupByCol = undefined
      this.groupByRow = undefined
      this.sortBy = undefined
      this.myAttributes = {}
      this.currentCustomer = undefined
      this.currentCustomerSegmentations = undefined
      this.currentTree = undefined

      for (const key in this.priceGroups) {
        this.priceGroups[key] = undefined
      }

      this.indexedSilhouetteImages = {}
      this.articleStateList = []
      this.masterSizeScales = {}
      this.sizeScales = {}
      this.indexedLookupAttributeDefinition = {}
      this.customSortArticlePropertyAndValues = {}
    },
    resetSellerData() {
      this.sellerDetails = undefined
      this.sellerConfig = undefined
    },
    setIndexedCatalogDetailsFromAdmin(catalogDetails: CatalogDetails) {
      if (!this.indexedCatalogDetailsFromAdmin[catalogDetails.CatalogCode]) {
        this.indexedCatalogDetailsFromAdmin[catalogDetails.CatalogCode] = catalogDetails
      }
    },
    resetIndexedCatalogDetailsFromAdmin() {
      this.indexedCatalogDetailsFromAdmin = {}
    },
    setIndexedRequestSourceArticlesFromAdmin(articleDetails: Article) {
      if (!this.indexedRequestSourceArticlesFromAdmin[articleDetails.Id]) {
        this.indexedRequestSourceArticlesFromAdmin[articleDetails.Id] = articleDetails
      }
    },
    resetIndexedRequestSourceArticlesFromAdmin() {
      this.indexedRequestSourceArticlesFromAdmin = {}
    },
    async setSettingsContainer() {
      // get the settings
      const settingsContainers = await utils.tryAsync(getMyContainers())
      if (settingsContainers.success) {
        const settingsContainerData = settingsContainers.result.data.find(container => container.ContainerName === personalSettingsConstants.settings)
        if (settingsContainerData) {
          const res = await utils.tryAsync(getContainer(settingsContainerData.Id))
          if (res.success) {
            const parsedValue = JSON.parse(res.result.data.Value)
            this.settingsContainer = parsedValue.data.userPreferences
            // set thumbnail size
            this.thumbnailSize = utils.isDefined(this.settingsContainer.general) && utils.isDefined(this.settingsContainer.general.thumbnailSize) ? thumbnailConstants.thumbnailSizeStrToNumberEnum[this.settingsContainer.general.thumbnailSize] : 3
          }
        }
      }
    },
    setMainNavBarVisibility(val: boolean) {
      this.mainNavBarVisibility = val
    },
    async setSellerData(sellerAccountId?: number) {
      const currentLocalAccountId = localStorage.getItem('currentAccountId')
      if (this.userProfile.AccountDetails.AccountTypeId === 2 || sellerAccountId || (utils.isDefined(currentLocalAccountId) && currentLocalAccountId !== '')) {
        const accountId = sellerAccountId || Number(currentLocalAccountId) || this.userProfile.AccountId
        this.sellerDetails = new SellerDetails(await appConfig.DB!.getSellerDetails(accountId))
        if (sellerAccountId) { // when admin user switch between accounts
          localStorage.setItem('currentAccountId', JSON.stringify(sellerAccountId))
        }
        const sellerConfigs = await getSellerConfigurations(accountId, this.userProfile.Roles)
        if (sellerConfigs) {
          this.sellerConfig = sellerConfigs
        }
      }
    },
  },
})
